import React from 'react';
import { Loan } from '../types';
import { Button } from './ui/Button';
import { LockIcon, TimerIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { getDaysInRetention } from '../utils/retention';
interface ReadyToClaimPanelProps {
  nextLoan?: Loan;
  queue: Loan[];
  onClaimNext: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function ReadyToClaimPanel({
  nextLoan,
  queue,
  onClaimNext
}: ReadyToClaimPanelProps) {
  const nextDays = nextLoan ? getDaysInRetention(nextLoan.dateEntered) : 0;
  return <motion.section key="empty" initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -10
  }} transition={{
    duration: 0.28,
    ease: EASE
  }} aria-labelledby="ready-heading" className="flex flex-1 items-center justify-center overflow-hidden bg-white px-6 py-10 sm:px-10" data-id="element-114" data-mp="3vsar1jrg7cet">
      <div className="flex w-full max-w-xl flex-col items-center text-center" data-id="element-115" data-mp="2zkjopysx3rnn">
        <span className="mb-2.5 block text-[11px] font-medium uppercase tracking-[0.14em] text-brand-300" data-id="element-116" data-mp="2zifmrmekx2q0">
          Queue ready
        </span>
        <h2 id="ready-heading" className="text-[32px] font-semibold leading-[1.1] tracking-[-0.03em] text-brand-500" data-id="element-117" data-mp="2pmvsf4fnyc3d">
          {queue.length === 0 ? 'The retention queue is clear.' : `${queue.length} loans are waiting to be worked.`}
        </h2>
        <p className="mt-3 max-w-md text-sm leading-6 text-brand-400" data-id="element-118" data-mp="2kn9j362yhfzu">
          {queue.length === 0 ? 'Nothing is waiting right now. New files will appear here the moment they are pushed to retention.' : 'Claim the next file in line to lock it to you for 60 minutes. Release it any time and it returns to the back of the queue.'}
        </p>

        <div className="mt-7 flex flex-col items-center gap-3" data-id="element-119" data-mp="21wi8603blhjc">
          <Button size="lg" onClick={onClaimNext} disabled={queue.length === 0} data-id="element-120" data-mp="21vqdqslglpun">
            Claim Next Loan
          </Button>
          <span className="flex items-center gap-2 text-[13px] text-brand-400" data-id="element-121" data-mp="1e16ins7ipfaw">
            <TimerIcon className="h-4 w-4 text-brand-300" data-id="element-122" data-mp="1dz3kum04ihvm" />
            60-minute claim window
          </span>
        </div>

        {queue.length > 0 && <>
            {nextLoan && <div className="mt-9 w-full rounded-2xl border border-brand-200 bg-brand-50 p-5 text-left" data-id="element-123" data-mp="140w7ed51mhyd">
                <span className="mb-4 block text-[11px] font-medium uppercase tracking-[0.12em] text-brand-300" data-id="element-124" data-mp="21oen67x4gkbv">
                  Next in line
                </span>
                <div className="flex items-start justify-between gap-3" data-id="element-125" data-mp="1rr06q8b77vjk">
                  <div className="min-w-0" data-id="element-126" data-mp="1mu9w40i5xs5j">
                    <p className="truncate text-[22px] font-semibold tracking-[-0.02em] text-brand-500" data-id="element-127" data-mp="1mvkutlwr49ps">
                      {nextLoan.borrowerName}
                    </p>
                    <p className="mt-1 font-mono text-xs text-brand-300" data-id="element-128" data-mp="bcrx1ebj1gpc">
                      {nextLoan.loanNumber}
                    </p>
                  </div>
                  <span className="flex shrink-0 items-center gap-1.5 rounded-full border border-brand-200 bg-white px-2.5 py-1 text-xs text-brand-400" data-id="element-129" data-mp="2s08cki08atnb">
                    <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${nextDays > 5 ? 'bg-rose-500' : nextDays > 2 ? 'bg-amber-500' : 'bg-emerald-500'}`} data-id="element-130" data-mp="2wxrp9dha2yd5" />
                    {nextDays}d
                  </span>
                </div>
                <dl className="mt-4 grid gap-3 border-t border-brand-200 pt-4 sm:grid-cols-2" data-id="element-131" data-mp="3at3u4dp5e57s">
                  <div data-id="element-132" data-mp="3fo9idj60vvz1">
                    <dt className="text-[13px] text-brand-400" data-id="element-133" data-mp="3kp6ube0fvts3">Loan amount</dt>
                    <dd className="mt-0.5 text-[15px] font-semibold text-brand-500" data-id="element-134" data-mp="3kn2sr2q0j6uz">
                      {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 0
                }).format(nextLoan.amount)}
                    </dd>
                  </div>
                  <div className="min-w-0" data-id="element-135" data-mp="3bsx0kpcuggf1">
                    <dt className="text-[13px] text-brand-400" data-id="element-136" data-mp="3psxwdqe94xnn">
                      Current owner
                    </dt>
                    <dd className="mt-0.5 truncate text-[15px] font-semibold text-brand-500" data-id="element-137" data-mp="3lsnt4lhuglch">
                      {nextLoan.loanOfficer}
                    </dd>
                  </div>
                  <div className="sm:col-span-2" data-id="element-138" data-mp="2t3gsngytxlol">
                    <dt className="text-[13px] text-brand-400" data-id="element-139" data-mp="2t0ucisxvhxur">
                      Retention reason
                    </dt>
                    <dd className="mt-0.5 text-[13px] leading-relaxed text-brand-500" data-id="element-140" data-mp="323vimtmqnoyf">
                      {nextLoan.retentionComment || 'No retention reason provided.'}
                    </dd>
                  </div>
                </dl>
              </div>}

            <p className="mt-5 flex items-center gap-2 text-xs text-brand-300" data-id="element-141" data-mp="208inkszq7r46">
              <LockIcon className="h-3.5 w-3.5" data-id="element-142" data-mp="206o47j43s727" />
              Claiming locks this file to you and hides it from the team.
            </p>
          </>}
      </div>
    </motion.section>;
}